import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, MapPin, ChevronRight, Loader2, CalendarDays, Hourglass, CheckCheck, Truck, Wrench, BadgeCheck, XCircle, Trophy, Star, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cachedGet } from "@/pages/tech/offline";
import { TechShell, StatusPill, inr } from "@/pages/tech/ui";

const CARDS = [["today", "Today's Jobs", CalendarDays], ["pending", "New / Pending", Hourglass], ["accepted", "Accepted", CheckCheck], ["on_the_way", "On the Way", Truck],
  ["in_progress", "In Progress", Wrench], ["completed", "Completed", BadgeCheck], ["cancelled", "Cancelled", XCircle]];
const TAB_KEYS = new Set(CARDS.map((c) => c[0]));

const Stats = ({ s, tab, setTab }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="tech-stats">
    {CARDS.map(([k, l, I]) => (
      <button key={k} onClick={() => setTab(k)} className={`card-soft p-4 text-left transition-colors ${tab === k ? "ring-2 ring-primary" : "hover:border-primary/50"}`} data-testid={`tech-stat-${k}`}>
        <div className="flex items-center justify-between text-xs text-muted-foreground">{l}<I className="h-4 w-4 text-primary" /></div>
        <p className="font-display text-2xl font-bold mt-1">{s?.[k] ?? "–"}</p>
      </button>
    ))}
    <div className="card-soft p-4 bg-primary text-primary-foreground" data-testid="tech-stat-summary">
      <p className="text-xs opacity-80 flex items-center gap-1"><Trophy className="h-3.5 w-3.5" />Total completed · <Star className="h-3.5 w-3.5" />{s?.rating ? `${s.rating.toFixed(1)} (${s.rating_count})` : "New"}</p>
      <p className="font-display text-2xl font-bold mt-1">{s?.total_completed ?? "–"}</p>
      <p className="text-xs opacity-80 flex items-center gap-1 mt-1"><Wallet className="h-3.5 w-3.5" />Collected {inr(s?.earnings)}</p>
    </div>
  </div>
);

const JobCard = ({ j }) => (
  <Link to={`/tech/jobs/${j.id}`} className="card-soft p-4 flex items-center gap-3 hover:border-primary/50 transition-colors" data-testid={`tech-job-${j.booking_no}`}>
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-primary">{j.booking_no}</span><StatusPill s={j.status} />{j.priority !== "standard" && <span className="text-xs font-semibold text-rose-600 capitalize">{j.priority.replace("_", " ")}</span>}</div>
      <p className="font-semibold">{j.service} · {j.name}</p>
      <p className="text-xs text-muted-foreground flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{j.preferred_date} · {j.preferred_time}</p>
      <p className="text-xs text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3.5 w-3.5 shrink-0" />{j.address}, {j.city} {j.pincode}</p>
    </div>
    <ChevronRight className="h-5 w-5 text-muted-foreground" />
  </Link>
);

export default function TechHome() {
  const [tab, setTabState] = useState(() => (TAB_KEYS.has(sessionStorage.getItem("tech-tab")) ? sessionStorage.getItem("tech-tab") : "today"));
  const [page, setPage] = useState(1);
  const setTab = (t) => { setTabState(t); setPage(1); sessionStorage.setItem("tech-tab", t); };
  useEffect(() => { document.title = "Technician Dashboard | Onkaal.com"; }, []);
  const { data: s } = useQuery({ queryKey: ["tech", "summary"], queryFn: () => cachedGet("/tech/summary"), refetchInterval: 60000 });
  const { data, isLoading, isError } = useQuery({ queryKey: ["tech", "jobs", tab, page], queryFn: () => cachedGet(`/tech/jobs?tab=${tab}&page=${page}`), refetchInterval: 60000 });
  return (
    <TechShell>
      <h1 className="text-2xl font-bold mb-4">My Jobs</h1>
      <Stats s={s} tab={tab} setTab={setTab} />
      <h2 className="font-semibold mt-6 mb-3" data-testid="tech-list-title">{CARDS.find((c) => c[0] === tab)?.[1]}</h2>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
        <div className="grid gap-3" data-testid="tech-job-list">
          {isError && <p className="text-sm text-muted-foreground card-soft p-6 text-center" data-testid="tech-jobs-offline">Can't load this list right now. If you're offline, open it again when internet is back.</p>}
          {data?.items.map((j) => <JobCard key={j.id} j={j} />)}
          {data?.items.length === 0 && <p className="text-sm text-muted-foreground card-soft p-6 text-center" data-testid="tech-jobs-empty">No jobs here right now.</p>}
        </div>
      )}
      <div className="flex justify-between mt-4">
        {page > 1 ? <Button variant="outline" onClick={() => setPage(page - 1)} data-testid="tech-jobs-prev">Previous</Button> : <span />}
        {data?.has_more && <Button variant="outline" onClick={() => setPage(page + 1)} data-testid="tech-jobs-next">Next</Button>}
      </div>
    </TechShell>
  );
}
